/**
 * Device Security and Trusted Device Management System
 * 
 * Rules:
 * 1. Always record and persist current device ID for users.
 * 2. On ANY new device (orderCountOnDevice === 0 / first purchase), mandate 6-digit PIN verification.
 * 3. On trusted device (orderCountOnDevice >= 1) with total <= 2500 THB, PIN is NOT required (bypassed).
 * 4. On high-value orders (total > 2500 THB), mandate 6-digit PIN verification ALWAYS regardless of device.
 */

export interface DeviceInfo {
  deviceId: string;
  userAgent: string;
  platform: string;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface UserDeviceRecord {
  deviceId: string;
  userKey: string;
  orderCount: number;
  firstOrderAt?: string;
  lastOrderAt?: string;
  lastUsedAt?: string;
  lastVerifiedAt?: string;
  lastOrderAmount?: number;
  lastOrderId?: string;
}

export interface PinRequirementCheck {
  requiresPin: boolean;
  reason: 'FIRST_ORDER_ON_NEW_DEVICE' | 'HIGH_VALUE_THRESHOLD' | 'NONE';
  title: string;
  description: string;
  orderCountOnDevice: number;
  deviceId: string;
  thresholdAmount: number;
}

const DEVICE_ID_KEY = 'pinkgiff_device_id';
const DEVICE_INFO_KEY = 'pinkgiff_device_info';
const LAST_ACTIVE_DEVICE_KEY = 'pinkgiff_last_active_device';
const HIGH_VALUE_THRESHOLD = 2500;

/**
 * Generate or retrieve permanent unique Device ID
 */
export function getOrCreateDeviceId(): string {
  try {
    let existingId = localStorage.getItem(DEVICE_ID_KEY);
    if (!existingId || existingId.trim() === '') {
      // Generate a crypto-strong unique device ID with timestamp
      const randomBytes = new Uint8Array(8);
      if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(randomBytes);
      } else {
        for (let i = 0; i < 8; i++) {
          randomBytes[i] = Math.floor(Math.random() * 256);
        }
      }
      const hex = Array.from(randomBytes).map(b => b.toString(16).padStart(2, '0')).join('');
      existingId = `DEV-${Date.now().toString(36).toUpperCase()}-${hex.toUpperCase()}`;
      localStorage.setItem(DEVICE_ID_KEY, existingId);

      const deviceInfo: DeviceInfo = {
        deviceId: existingId,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        platform: typeof navigator !== 'undefined' ? (navigator.platform || 'web') : 'web',
        firstSeenAt: new Date().toISOString(),
        lastSeenAt: new Date().toISOString(),
      };
      localStorage.setItem(DEVICE_INFO_KEY, JSON.stringify(deviceInfo));
    } else {
      // Update last seen
      try {
        const raw = localStorage.getItem(DEVICE_INFO_KEY);
        if (raw) {
          const info: DeviceInfo = JSON.parse(raw);
          info.lastSeenAt = new Date().toISOString();
          localStorage.setItem(DEVICE_INFO_KEY, JSON.stringify(info));
        }
      } catch (e) {}
    }
    return existingId;
  } catch (err) {
    console.warn('[DeviceSecurity] localStorage not available:', err);
    return 'DEV-TEMP-DEFAULT';
  }
}

/**
 * Normalize user key from customerId, memberId, email, or userId
 */
export function normalizeUserKey(userOrIdentifier?: any): string {
  if (!userOrIdentifier) return 'GUEST_USER';
  if (typeof userOrIdentifier === 'string') {
    const trimmed = userOrIdentifier.trim().toUpperCase();
    return trimmed || 'GUEST_USER';
  }
  const candidate = 
    userOrIdentifier.customerId ||
    userOrIdentifier.memberId ||
    userOrIdentifier.id ||
    userOrIdentifier.uid ||
    userOrIdentifier.email ||
    'GUEST_USER';
  return String(candidate).trim().toUpperCase();
}

/**
 * Get storage key for user trusted devices
 */
function getUserDevicesStoreKey(userKey: string): string {
  return `pinkgiff_user_devices_${userKey}`;
}

/**
 * Get all devices registered to this user
 */
export function getUserDevices(userKey: string): UserDeviceRecord[] {
  try {
    const normKey = normalizeUserKey(userKey);
    const raw = localStorage.getItem(getUserDevicesStoreKey(normKey));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (err) {
    console.error('[DeviceSecurity] Error reading user devices:', err);
  }
  return [];
}

/**
 * Get device record for a specific user and deviceId
 */
export function getUserDeviceRecord(userKey: string, deviceId: string): UserDeviceRecord | null {
  const devices = getUserDevices(userKey);
  const found = devices.find(d => d.deviceId === deviceId);
  return found || null;
}

/**
 * Core Security Rule Logic:
 * Check if the checkout requires 6-digit PIN verification
 */
export function checkCheckoutPinRequirement(params: {
  userKey?: any;
  totalAmount: number;
}): PinRequirementCheck {
  const deviceId = getOrCreateDeviceId();
  const normKey = normalizeUserKey(params.userKey);
  const totalAmount = Number(params.totalAmount) || 0;
  
  const deviceRecord = getUserDeviceRecord(normKey, deviceId);
  const orderCountOnDevice = deviceRecord ? deviceRecord.orderCount : 0;

  // RULE 1: High-Value Threshold (> 2,500 THB) -> ALWAYS require PIN
  if (totalAmount > HIGH_VALUE_THRESHOLD) {
    return {
      requiresPin: true,
      reason: 'HIGH_VALUE_THRESHOLD',
      title: 'ยืนยันความปลอดภัยคำสั่งซื้อมูลค่าสูง (> ฿2,500)',
      description: `คำสั่งซื้อนี้มียอดรวม ฿${totalAmount.toLocaleString()} บาท ซึ่งเกินเกณฑ์ความปลอดภัย ฿${HIGH_VALUE_THRESHOLD.toLocaleString()} บาท กรุณากรอกรหัส PIN 6 หลักเพื่อยืนยันการทำรายการ`,
      orderCountOnDevice,
      deviceId,
      thresholdAmount: HIGH_VALUE_THRESHOLD
    };
  }

  // RULE 2: First order on new / untrusted device -> REQUIRE PIN
  if (!deviceRecord || orderCountOnDevice === 0) {
    return {
      requiresPin: true,
      reason: 'FIRST_ORDER_ON_NEW_DEVICE',
      title: 'ยืนยันตัวตนสำหรับการสั่งซื้อครั้งแรกบนอุปกรณ์นี้',
      description: 'ระบบตรวจพบการสั่งซื้อครั้งแรกบนอุปกรณ์ใหม่ เพื่อความปลอดภัยของข้อมูลและแต้มสะสม PV กรุณากรอกรหัส PIN 6 หลัก',
      orderCountOnDevice: 0,
      deviceId,
      thresholdAmount: HIGH_VALUE_THRESHOLD
    };
  }

  // RULE 3: Device is trusted (orderCount >= 1) and totalAmount <= 2,500 THB -> NO PIN NEEDED
  return {
    requiresPin: false,
    reason: 'NONE',
    title: 'อุปกรณ์เดิมที่ผ่านการยืนยันแล้ว',
    description: 'ระบบข้ามการกรอก PIN อัตโนมัติสำหรับการสั่งซื้อครั้งที่ 2 ขึ้นไปบนอุปกรณ์เดิม',
    orderCountOnDevice,
    deviceId,
    thresholdAmount: HIGH_VALUE_THRESHOLD
  };
}

/**
 * Record successful order completion on this device
 * Updates the order count and marks this device as trusted
 */
export function recordDeviceOrderSuccess(params: {
  userKey?: any;
  orderId: string;
  totalAmount: number;
  deviceId?: string;
}): void {
  try {
    const deviceId = params.deviceId || getOrCreateDeviceId();
    const normKey = normalizeUserKey(params.userKey);
    const now = new Date().toISOString();

    // 1. Update User Devices list
    const devices = getUserDevices(normKey);
    const existingIndex = devices.findIndex(d => d.deviceId === deviceId);

    if (existingIndex >= 0) {
      devices[existingIndex].orderCount = (devices[existingIndex].orderCount || 0) + 1;
      devices[existingIndex].lastOrderAt = now;
      devices[existingIndex].lastUsedAt = now;
      devices[existingIndex].lastOrderId = params.orderId;
      devices[existingIndex].lastOrderAmount = params.totalAmount;
      devices[existingIndex].lastVerifiedAt = now;
    } else {
      devices.push({
        deviceId,
        userKey: normKey,
        orderCount: 1,
        firstOrderAt: now,
        lastOrderAt: now,
        lastUsedAt: now,
        lastOrderId: params.orderId,
        lastOrderAmount: params.totalAmount,
        lastVerifiedAt: now
      });
    }

    localStorage.setItem(getUserDevicesStoreKey(normKey), JSON.stringify(devices));

    // 2. Update Global Last Active Device
    const lastActiveData = {
      deviceId,
      userKey: normKey,
      lastOrderId: params.orderId,
      lastOrderAmount: params.totalAmount,
      timestamp: now,
      orderCountOnDevice: existingIndex >= 0 ? devices[existingIndex].orderCount : 1
    };
    localStorage.setItem(LAST_ACTIVE_DEVICE_KEY, JSON.stringify(lastActiveData));

    console.log(`[DeviceSecurity] Recorded successful order #${params.orderId} for ${normKey} on Device ${deviceId}. Total device orders: ${lastActiveData.orderCountOnDevice}`);
  } catch (err) {
    console.error('[DeviceSecurity] Failed to record order success on device:', err);
  }
}

/**
 * Get the latest active device info
 */
export function getLastActiveDeviceInfo(): any {
  try {
    const raw = localStorage.getItem(LAST_ACTIVE_DEVICE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return null;
}
